import classNames from "classnames/dedupe";
import React, { useCallback, useMemo, useRef } from "react";
import deepClone from "../../../../helpers/deepClone";
import useOnClickOutside from "../../../../hooks/useOnClickOutside";
import useUpdateEffect from "../../../../hooks/useUpdateEffect";
import useWatchState from "../../../../hooks/useWatchState";
import { DownIconSmall, UpIconSmall } from "../../../../icons";
import { Typography } from "../../../Fundamentals/Typography/Typography";
import SvgIcon from "../../../Icons/SvgIcon";
import DrawerMask from "../../../Utility/DrawerMask/DrawerMask";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import {
  DropdownAlignment,
  DropdownToggleStrategy,
} from "../../../Utility/Dropdown/Dropdown.interface";
import DropdownList from "../../../Utility/Dropdown/DropdownList";
import ScrollContainer from "../../../Utility/ScrollContainer/ScrollContainer";
import useTheme from "../../../Utility/ThemeProvider/useTheme";
import { ClearAll } from "../ClearAll/ClearAll";
import { DropdownOption } from "../DropdownOption/DropdownOption";
import { ISelectOption, ISelectProps } from "./Select.interface";

export default function Select<Option = ISelectOption, IsMulti = false>({
  classes,
  styles,
  options = [],
  variant = "default",
  opened,
  theme: propsTheme = "dark",
  buttonTheme: propsButtonTheme = "light",
  placeholder = "Select an option",
  mobilePlaceholder = placeholder,
  noOptionsPlaceholder = "No options available",
  dropdownSize = "default",
  labels,
  value,
  valueKey = "id" as keyof Option,
  labelKey = "label" as keyof Option,
  titlePrefix,
  customOptionRender,
  renderOptionsHeader,
  customButtonContentRender,
  multiple,
  disabled,
  clearAll,
  error,
  errorMessage,
  closeMenuOnSelect = true,
  toggleToFlyoutOnMobile: toggleToFlyoutOnMobileProps = true,
  alignment = DropdownAlignment.FIT,
  toggleStrategy = DropdownToggleStrategy.CLICK,
  onDropdownToggle,
  onChange,
  dropdownOptions,
}: ISelectProps<Option, IsMulti>): JSX.Element {
  const theme = useTheme(propsTheme);
  const buttonTheme = useTheme(propsButtonTheme);

  const [dropdownOpened, setDropdownOpened] = useWatchState({ value: opened });

  const selected = useMemo(() => {
    if (multiple) {
      return value && Array.isArray(value)
        ? options.filter((option) => value.includes(option[valueKey]))
        : [];
    }
    return options.find((option) => option[valueKey] === value);
  }, [multiple, options, value, valueKey]);

  const hasSelectedValue = useMemo(() => {
    return !!(multiple
      ? selected && Array.isArray(selected) && selected.length
      : selected);
  }, [multiple, selected]);

  const readOnly = !(options.length < 2 && hasSelectedValue);

  const buttonClasses = useMemo(
    () =>
      classNames(
        variant === "rounded"
          ? "dropdown-toggle"
          : {
              "dropdown-toggle-rectangle": buttonTheme === "light",
              "dropdown-toggle-rectangle dropdown-toggle-blue":
                buttonTheme === "dark",
              "dropdown-toggle-rectangle with-warning": error === true,
              selected: hasSelectedValue,
              "dropdown-toggle-rectangle--blue":
                !readOnly && buttonTheme === "light",
              "dropdown-toggle-rectangle--white":
                !readOnly && buttonTheme === "dark",
            },
        classes?.button,
        {
          "cursor-default": disabled || !readOnly,
        },
      ),
    [variant, buttonTheme, error, hasSelectedValue, readOnly, classes],
  );

  const handleOptionToggle = useCallback(
    (newValueFromOption) => {
      // eslint-disable-next-line prefer-const
      let current = multiple ? deepClone(value) : value;
      let result;
      if (multiple) {
        if (current && Array.isArray(current)) {
          if (current.includes(newValueFromOption)) {
            current.splice(current.indexOf(newValueFromOption), 1);
          } else {
            current.push(newValueFromOption);
          }
        }
        result = current;
      } else {
        result = newValueFromOption;
      }
      if (closeMenuOnSelect) {
        setDropdownOpened(false);
      }
      onChange && onChange(result);
    },
    [closeMenuOnSelect, multiple, onChange, setDropdownOpened, value],
  );

  const handleOpenedChange = useCallback(
    (newOpened) => setDropdownOpened(newOpened),
    [setDropdownOpened],
  );

  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const handleClickOutside = useCallback(
    () => setDropdownOpened(false),
    [setDropdownOpened],
  );

  useOnClickOutside(handleClickOutside, "mousedown", buttonRef, listRef);

  useUpdateEffect(() => {
    onDropdownToggle && onDropdownToggle(dropdownOpened);
  }, [onDropdownToggle, dropdownOpened]);

  const buttonContent = useMemo(() => {
    if (customButtonContentRender)
      return customButtonContentRender({
        options,
        variant,
        labels,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        selected,
        hasSelectedValue,
        placeholder,
        multiple,
        labelKey,
        valueKey,
        titlePrefix,
      });
    if (!hasSelectedValue) return placeholder;
    return (
      <span className="max-lines max-lines--1 white-space-nowrap">
        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
        <>
          {titlePrefix || ""}
          {multiple && Array.isArray(selected) && selected.length
            ? selected.map((item) => item[labelKey]).join(", ")
            : (selected as Option)[labelKey]}
        </>
      </span>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelKey, multiple, placeholder, selected]);

  const handleMaskClick = useCallback(
    () => setDropdownOpened(false),
    [setDropdownOpened],
  );

  const handleClearAll = () => {
    onChange && onChange(multiple ? [] : (undefined as any));
    clearAll?.clearAllCallback && clearAll?.clearAllCallback();
  };

  const sideIcon = dropdownOpened ? (
    <UpIconSmall className="ms-3" />
  ) : (
    <DownIconSmall className="ms-3" />
  );

  return (
    <Dropdown
      disabled={disabled || !readOnly}
      theme={theme}
      buttonTheme={buttonTheme}
      variant={variant}
      opened={dropdownOpened}
      dropdownButton={
        <div
          ref={buttonRef}
          className={classNames("dropdown dropdown--full-width", classes?.root)}
          data-testid="Select__dropdown-button"
        >
          <button
            className={buttonClasses}
            disabled={disabled || !readOnly}
            style={styles?.button}
            type="button"
            data-testid="selector-button"
          >
            {buttonContent}
            {!disabled && (
              <div
                style={{
                  visibility: !readOnly ? "hidden" : "visible",
                  lineHeight: 0,
                }}
                data-testid="Select__side_icon"
              >
                {sideIcon}
              </div>
            )}
          </button>
          {error && (
            <span className="dropdown-warning-message">{errorMessage}</span>
          )}
        </div>
      }
      toggleToFlyoutOnMobile={toggleToFlyoutOnMobileProps}
      alignment={alignment}
      toggleStrategy={toggleStrategy}
      onChange={handleOpenedChange}
      {...(dropdownOptions || {})}
    >
      <DropdownList animation="uds-dropdown-toggle">
        {({
          toggleToFlyoutOnMobile,
          opened: dropdownStateOpened,
          setOpened,
        }) => (
          <div
            className={classNames(
              "dropdown-menu position-fixed position-md-relative w-auto left-0 top-0 mt-3",
              {
                show: dropdownStateOpened,
                "dropdown-menu--dark": theme === "dark",
                "dropdown-menu--flyout": toggleToFlyoutOnMobile,
              },
            )}
            ref={listRef}
          >
            {toggleToFlyoutOnMobile ? (
              <DrawerMask
                classes={{ root: "d-block d-md-none" }}
                onClick={handleMaskClick}
              />
            ) : null}
            <div
              className={classNames("dropdown-menu__content-wrapper", {
                "dropdown-menu__content-wrapper--with-min-width":
                  dropdownSize === "default",
                "dropdown-menu__content-wrapper--dark": theme === "dark",
                "dropdown-menu__content-wrapper--light": theme === "light",
                "dropdown-menu__content-wrapper--flyout":
                  toggleToFlyoutOnMobile,
              })}
            >
              <div className="dropdown-menu__mobile-header">
                <Typography
                  classes={{
                    root: "dropdown-menu__mobile-header__name w-auto flex-grow-1",
                  }}
                  variant="mobile-header-12-bold"
                  as="div"
                >
                  {mobilePlaceholder}
                </Typography>
                <SvgIcon
                  data-testid="dropdown-menu__close-icon"
                  type="svg-icon"
                  icon="ExitIcon"
                  isButton
                  onClick={() => setOpened(false)}
                />
              </div>
              <div className="dropdown-menu-body">
                <ScrollContainer height="auto">
                  {renderOptionsHeader?.()}
                  {!options.length && (
                    <div className="bg-color-blue text-color-white h-menu-medium">
                      {noOptionsPlaceholder}
                    </div>
                  )}
                  {options.map((option) => {
                    const isOptionSelected = multiple
                      ? selected &&
                        Array.isArray(selected) &&
                        !!selected.find(
                          (opt) => opt[valueKey] === option[valueKey],
                        )
                      : value === option[valueKey];
                    const key = option[valueKey] as unknown as string;

                    return customOptionRender ? (
                      <Typography as="div" key={key}>
                        {customOptionRender({
                          item: option,
                          labels,
                          valueKey,
                          labelKey,
                          selected: isOptionSelected,
                          titlePrefix,
                          onSelect: handleOptionToggle,
                        })}
                      </Typography>
                    ) : (
                      <DropdownOption<Option>
                        key={key}
                        item={option}
                        labelKey={labelKey}
                        valueKey={valueKey}
                        selected={isOptionSelected}
                        onSelect={handleOptionToggle}
                      />
                    );
                  })}
                </ScrollContainer>
              </div>
              {multiple && clearAll && hasSelectedValue ? (
                <ClearAll
                  text={clearAll?.clearAllText}
                  onClear={handleClearAll}
                />
              ) : null}
            </div>
          </div>
        )}
      </DropdownList>
    </Dropdown>
  );
}

/*
const WithForwardSelect = React.forwardRef(Select) as <Option, IsMulti = false>(
  props: ISelectProps<Option, IsMulti> & {
    ref?: React.ForwardedRef<HTMLSelectElement>;
  },
) => ReturnType<typeof Select>;

export default WithForwardSelect;
*/
